import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateBR, formatMoneyBR, pct } from "../../utils/format";

type ExecData = {
  balances?: {
    consolidated?: number;
  };
  payablesDue?: {
    days7?: { total: number };
    days15?: { total: number };
    days30?: { total: number };
  };
  receivablesDue?: {
    days7?: { total: number };
    days15?: { total: number };
    days30?: { total: number };
  };
  monthResult?: {
    net: number;
    totalIn: number;
    totalOut: number;
  };
  budgetVsActual?: {
    budgeted: number;
    actual: number;
    variance: number;
    variancePercent: number;
  };
  topExpenses?: {
    byCategory: Array<{ label: string; total: number }>;
  };
  topRevenues?: {
    byCategory: Array<{ label: string; total: number }>;
  };
  cashFlowSeries?: Array<{
    period: string;
    totalIn: number;
    totalOut: number;
    net: number;
  }>;
};

type PayableDetail = {
  id: number;
  description: string;
  amount: number;
  dueDate: string;
};

type ReceivableDetail = {
  id: number;
  description: string;
  amount: number;
  dueDate: string;
};

type MovementDetail = {
  id: number;
  description: string;
  type: string;
  amount: number;
  occurredAt: string;
};

export const ExecDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>(() => getCurrentMonthRange());
  const [data, setData] = useState<ExecData | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/dash/exec?${query}`);
      if (!response.ok) {
        console.error("Exec dashboard failed", { status: response.status });
        setData(null);
        setError("Falha ao carregar dashboard.");
        setLoading(false);
        return;
      }
      setData((await response.json()) as ExecData);
      setLoading(false);
    };
    fetchData();
  }, [client, query]);

  const payablesQuery = usePagedQuery<PayableDetail>({
    endpoint: "/dash/exec/vencimentos/ap",
    filters: { ...filters, days: 7 },
  });

  const receivablesQuery = usePagedQuery<ReceivableDetail>({
    endpoint: "/dash/exec/vencimentos/ar",
    filters: { ...filters, days: 7 },
  });

  const movementsQuery = usePagedQuery<MovementDetail>({
    endpoint: "/dash/exec/top-receitas/detalhe",
    filters,
  });

  const cards = [
    {
      key: "payables",
      title: "Contas a pagar vencendo",
      value: data?.payablesDue?.days7?.total ?? 0,
      subtitle: "7 dias",
      detail: "payables",
    },
    {
      key: "receivables",
      title: "Contas a receber vencendo",
      value: data?.receivablesDue?.days7?.total ?? 0,
      subtitle: "7 dias",
      detail: "receivables",
    },
    {
      key: "result",
      title: "Resultado do periodo",
      value: data?.monthResult?.net ?? 0,
      subtitle: "Entradas - Saidas",
      detail: "movements",
    },
    {
      key: "budget",
      title: "Orçado vs Realizado",
      value: data?.budgetVsActual?.variance ?? 0,
      subtitle: `${pct(data?.budgetVsActual?.variancePercent ?? 0, 1)} executado`,
      detail: null,
      extra: {
        budgeted: data?.budgetVsActual?.budgeted ?? 0,
        actual: data?.budgetVsActual?.actual ?? 0,
      },
    },
  ];

  const cashFlowSeries =
    data?.cashFlowSeries?.map((item) => ({
      period: item.period,
      totalIn: Number(item.totalIn),
      totalOut: Number(item.totalOut),
      net: Number(item.net),
    })) ?? [];

  const topExpenses = data?.topExpenses?.byCategory ?? [];
  const topRevenues = data?.topRevenues?.byCategory ?? [];

  return (
    <div className="container py-4">
      <h1 className="mb-3">Dashboard Executivo</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />
      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row g-3">
        {cards.map((card) => (
          <div className="col-md-3" key={card.key}>
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted">{card.title}</h6>
                {card.key === 'budget' && card.extra ? (
                  <div>
                    <div className="mb-2">
                      <small className="text-muted">Orçado:</small>
                      <h5 className="text-primary mb-0">
                        {formatMoneyBR(card.extra.budgeted)}
                      </h5>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Realizado:</small>
                      <h5 className="text-success mb-0">
                        {formatMoneyBR(card.extra.actual)}
                      </h5>
                    </div>
                    <div>
                      <small className="text-muted">Variação:</small>
                      <h5 className={Number(card.value) >= 0 ? "text-success mb-0" : "text-danger mb-0"}>
                        {formatMoneyBR(card.value)}
                      </h5>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3>{formatMoneyBR(card.value)}</h3>
                    <p className="text-muted">{card.subtitle}</p>
                  </>
                )}
                {card.detail && (
                  <button
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={() => setDetail(card.detail)}
                  >
                    Ver detalhes
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-8">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Fluxo realizado</h5>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={cashFlowSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="totalIn" name="Entradas" stroke="#198754" />
                    <Line type="monotone" dataKey="totalOut" name="Saidas" stroke="#dc3545" />
                    <Line type="monotone" dataKey="net" name="Resultado" stroke="#0d6efd" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="text-muted">Saldo consolidado</h6>
              <h3>{formatMoneyBR(data?.balances?.consolidated ?? 0)}</h3>
              <p className="text-muted mb-0">Total conciliado no periodo</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Top despesas</h5>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={topExpenses}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" hide />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total" fill="#dc3545" name="Despesas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Top receitas</h5>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={topRevenues}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" hide />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total" fill="#198754" name="Receitas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {detail === "payables" && (
          <PageState loading={payablesQuery.loading} error={payablesQuery.error}>
            <DataTable<PayableDetail>
              title="Contas a pagar vencendo (7 dias)"
              rows={payablesQuery.rows}
              total={payablesQuery.total}
              page={payablesQuery.page}
              take={payablesQuery.take}
              onPageChange={payablesQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Vencimento", render: (item) => formatDateBR(item.dueDate) },
              ]}
            />
          </PageState>
        )}
        {detail === "receivables" && (
          <PageState loading={receivablesQuery.loading} error={receivablesQuery.error}>
            <DataTable<ReceivableDetail>
              title="Contas a receber vencendo (7 dias)"
              rows={receivablesQuery.rows}
              total={receivablesQuery.total}
              page={receivablesQuery.page}
              take={receivablesQuery.take}
              onPageChange={receivablesQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Vencimento", render: (item) => formatDateBR(item.dueDate) },
              ]}
            />
          </PageState>
        )}
        {detail === "movements" && (
          <PageState loading={movementsQuery.loading} error={movementsQuery.error}>
            <DataTable<MovementDetail>
              title="Movimentacoes do periodo"
              rows={movementsQuery.rows}
              total={movementsQuery.total}
              page={movementsQuery.page}
              take={movementsQuery.take}
              onPageChange={movementsQuery.setPage}
              columns={[
                { label: "Descricao", render: (item) => item.description },
                { label: "Tipo", render: (item) => item.type },
                { label: "Valor", render: (item) => formatMoneyBR(item.amount) },
                { label: "Data", render: (item) => formatDateBR(item.occurredAt) },
              ]}
            />
          </PageState>
        )}
      </div>
    </div>
  );
};
